import { useTheme } from '../../context/ThemeProvider';
import { useAppContext } from '../../context/AppContext';
import { useFullScreen } from '../../hooks/useFullScreen';

const TopBar = () => {
  const { setTheme, currentTheme } = useTheme();
  const { dispatch } = useAppContext();
  const { isFullScreen, toggleFullScreen } = useFullScreen();

  const handleThemeToggle = () => {
    if (currentTheme === 'light') {
      setTheme('dark');
    } else if (currentTheme === 'dark') {
      setTheme('system');
    } else {
      setTheme('light');
    }
  };

  const handleExportClick = () => {
    dispatch({ type: 'TOGGLE_EXPORT_MODAL' });
  };

  const getThemeIcon = () => {
    switch (currentTheme) {
      case 'light':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'dark':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        );
      case 'system':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
    }
  };

  const getExportIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );

  const getFullScreenIcon = () => (
    isFullScreen ? (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
          d="M6 18L18 6M6 6l12 12" />
      </svg>
    ) : (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
          d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
      </svg>
    )
  );

  return (
    <div className="h-14 border-b bg-white dark:bg-gray-800 cs-animate-fade-in" 
         style={{
           borderColor: 'var(--cs-gray-200)',
           boxShadow: 'var(--cs-shadow-sm)'
         }}>
      <div className="h-full max-w-full px-4 md:px-6 flex items-center justify-between">
        {/* Left section - Enhanced Brand with Logo */}
        <div className="flex items-center space-x-2 md:space-x-3">
          {/* Content Studio Logo - Responsive sizing */}
          <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl overflow-hidden flex-shrink-0">
            <img 
              src="/favicon.png" 
              alt="Content Studio" 
              className="w-full h-full object-contain"
              loading="eager"
            />
          </div>
          
          {/* Brand Text - Responsive Typography */}
          <div className="flex flex-col min-w-0">
            <h1 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight truncate" 
                style={{ fontFamily: 'var(--cs-font-family-sans)', fontWeight: 'var(--cs-font-weight-semibold)' }}>
              <span className="hidden sm:inline">Content Studio</span>
              <span className="sm:hidden">CS</span>
            </h1>
            <span className="hidden md:block text-xs lg:text-sm text-gray-500 dark:text-gray-400 -mt-0.5 truncate"
                  style={{ fontFamily: 'var(--cs-font-family-sans)', fontWeight: 'var(--cs-font-weight-normal)' }}>
              Markdown Editor
            </span>
          </div>
        </div>

        {/* Center section - Status/Info (hidden on mobile) */}
        <div className="hidden lg:flex items-center space-x-6 text-sm text-gray-600 dark:text-gray-400">
          {/* Document status could go here in future */}
        </div>

        {/* Right section - Enhanced Controls */}
        <div className="flex items-center space-x-1 md:space-x-2">
          {/* Export button */}
          <button
            onClick={handleExportClick}
            className="cs-btn-secondary cs-btn-sm cs-hover-lift group cursor-pointer"
            title="Export document (Ctrl+K)"
            style={{ fontFamily: 'var(--cs-font-family-sans)', fontWeight: 'var(--cs-font-weight-medium)' }}
          >
            {getExportIcon()}
            <span className="hidden sm:inline ml-1 whitespace-nowrap">Export</span>
            <kbd className="hidden md:inline ml-2 px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-400 whitespace-nowrap"
                 style={{ fontFamily: 'var(--cs-font-family-mono)', fontWeight: 'var(--cs-font-weight-normal)' }}>
              ⌘K
            </kbd>
          </button>

          {/* Divider */}
          <div className="hidden sm:block w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1"></div>

          {/* Theme toggle */}
          <button
            onClick={handleThemeToggle}
            className="cs-btn-ghost cs-btn-sm cs-hover-lift cursor-pointer"
            title={`Theme: ${currentTheme}. Click to cycle themes.`}
            style={{ fontFamily: 'var(--cs-font-family-sans)', fontWeight: 'var(--cs-font-weight-medium)' }}
          >
            {getThemeIcon()}
            <span className="hidden md:inline ml-1 capitalize whitespace-nowrap min-w-[60px] text-left">{currentTheme}</span>
          </button>

          {/* Full-screen toggle (desktop only) */}
          <button
            onClick={toggleFullScreen}
            className="hidden md:flex cs-btn-ghost cs-btn-sm cs-hover-lift cursor-pointer"
            title={isFullScreen ? "Exit full-screen (F11)" : "Enter full-screen (F11)"}
            style={{ fontFamily: 'var(--cs-font-family-sans)', fontWeight: 'var(--cs-font-weight-medium)' }}
          >
            {getFullScreenIcon()}
            <span className="ml-1 whitespace-nowrap min-w-[40px] text-left">
              {isFullScreen ? 'Exit' : 'Focus'}
            </span>
          </button>
        </div>
      </div>
      
      {/* Subtle gradient overlay for depth */}
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent dark:via-gray-700"></div>
    </div>
  );
};

export default TopBar;